import { useEffect, useRef } from 'react';
import Box from '@mui/material/Box';
import type { BoxProps } from '@mui/material/Box';
import { getSafeHtml, type SafeHtmlPolicy } from '@/lib/securityValidation';

type SafeHtmlProps = Readonly<
  Omit<BoxProps, 'dangerouslySetInnerHTML' | 'children'> & {
    html: string;
    /** `'report'` keeps `<style>` / inline styles for SSRS clinical summaries. */
    policy?: SafeHtmlPolicy;
    /**
     * Mount sanitized HTML in a shadow root so its `<style>` blocks stay inside
     * this host. Required for SSRS clinical summaries, whose styles target
     * `body` / `div` / `table` and would otherwise restyle the portal.
     */
    isolate?: boolean;
  }
>;

/**
 * Renders HTML after sanitizing with DOMPurify via getSafeHtml.
 * The single allowed dangerouslySetInnerHTML call site for Semgrep.
 * `isolate` uses shadow-root innerHTML instead, still fed only by getSafeHtml.
 */
export function SafeHtml({ html, policy = 'default', isolate = false, component = 'div', ...rest }: SafeHtmlProps) {
  const sanitized = getSafeHtml(html, policy);
  const hostRef = useRef<HTMLElement>(null);

  useEffect(() => {
    if (!isolate) return;
    const host = hostRef.current;
    if (!host) return;
    const root = host.shadowRoot ?? host.attachShadow({ mode: 'open' });
    // nosemgrep: javascript.browser.security.insecure-document-method.insecure-document-method -- DOMPurify via getSafeHtml
    root.innerHTML = sanitized;
    // Host `sx` selectors cannot reach shadow content. Keep report images inside the sheet.
    // Inherited color/background from a dark-mode host would otherwise tint transparent
    // SSRS markup; :host only sets the canvas defaults — report CSS still wins on its nodes.
    const fit = root.ownerDocument.createElement('style');
    fit.textContent = ':host{display:block;background:#fff;color:#000}img{max-width:100%;height:auto}';
    root.appendChild(fit);
  }, [isolate, sanitized]);

  if (isolate) {
    return <Box component={component} {...rest} ref={hostRef} />;
  }

  return (
    <Box
      component={component}
      {...rest}
      // nosemgrep: eslint.react-dangerouslysetinnerhtml
      dangerouslySetInnerHTML={{ __html: sanitized }} // nosemgrep: eslint.react-dangerouslysetinnerhtml -- DOMPurify via getSafeHtml
    />
  );
}
